import React, { Fragment, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import {
  Button,
  clsx,
  Modal,
  SvgIcon,
  Typography,
  useBoolean,
  Nullable,
} from "@gemlightbox/core-kit";

import { useStores } from "src/hooks";
import { proFeatureTooltip } from "src/utils";
import { AR_MEDIA_PAGE } from "src/constants";
import { supportedTypes, productTypesMap } from "src/external-ts/pages/ar-media/ar-media.constants";
import {
  MediaType,
  productDefaultTypesNames,
  ProductTypeModel,
  ProductTypeOutputV2,
} from "src/models";
import { editMediaStore } from "../../../edit-media.store";
import { GroupBlock } from "../../group-block";

import { ReactComponent as ProBadgeSVG } from "src/external-ts/assets/images/edit-media/pro-badge.svg";
import styles from "./apply-model-template.module.css";
import { LocaleCodeTypes } from "src/store/locale/locale-generated.store.types";

const matchProductType = (
  productTypes: ProductTypeOutputV2[],
  productTypeName?: Nullable<string>,
): Nullable<ProductTypeOutputV2> => {
  if (!productTypeName) return null;
  const normalized = productTypeName.toLowerCase();
  return (
    productTypes.find((item) => {
      const normalizedName = item.name?.toLowerCase();
      const normalizedKey = item.key?.toLowerCase();
      if (normalized === "pendant") {
        return (
          normalizedName === "pendants" ||
          normalizedKey === "pendant" ||
          normalizedKey === "pendants"
        );
      }
      return normalizedName === normalized || normalizedKey === normalized;
    }) || null
  );
};

export const ApplyModelTemplate: React.FC = observer(() => {
  const {
    localeStore,
    subscriptionsStore,
    tooltipStore,
    categoriesStore,
    notificationStore,
    mediaStore,
  } = useStores();
  const navigate = useNavigate();
  const { mediaId } = useParams<{ mediaId: string }>();

  const chooseTypeModal = useBoolean(false);
  const [selectedType, setSelectedType] = useState<Nullable<ProductTypeModel>>(null);

  const isProLocked = !subscriptionsStore.isSubscribed;

  const productTypes = useMemo(() => {
    const supportedTypeKeys = supportedTypes.map((type) => type.toLowerCase());
    return categoriesStore.categoriesV2
      .filter(({ mainTypeKey }) => !mainTypeKey)
      .filter(({ name, key }) => {
        const normalizedName = name?.toLowerCase() || "";
        const normalizedKey = key?.toLowerCase() || "";
        return (
          supportedTypeKeys.includes(normalizedName) || supportedTypeKeys.includes(normalizedKey)
        );
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [categoriesStore.categoriesV2]);

  const productTypesTemplates = useMemo(() => {
    return categoriesStore.defaultCategories
      .filter(({ name, showInARFlow }) => {
        return showInARFlow && supportedTypes.indexOf(name) !== -1;
      })
      .sort((a, b) => a.position - b.position);
  }, [categoriesStore.defaultCategories]);

  const resolveExistingProductType = () => {
    const media = editMediaStore.mediaShortModel;
    const product_type_key = (media as any)?.product?.product_type_key;
    const productTypeName =
      (media as any)?.arData?.productType ||
      (media as any)?.product?.productType ||
      (media as any)?.productType;

    if (product_type_key) {
      const foundCategory = categoriesStore.categoriesV2.find(
        (item) => item.key === product_type_key,
      );
      if (foundCategory) {
        const mainCategoryKey = foundCategory.mainTypeKey || foundCategory.key;
        const mainCategory = productTypes.find((item) => item.key === mainCategoryKey);
        if (mainCategory) return mainCategory;
      }
      const fallbackCategory = productTypes.find((item) => item.key === product_type_key);
      if (fallbackCategory) return fallbackCategory;
    }

    return matchProductType(productTypes, productTypeName);
  };

  const openEditor = (productTypeName?: string) => {
    if (!mediaId) return;

    const existingType = resolveExistingProductType();
    const targetType =
      productTypeName || selectedType?.name || existingType?.name || existingType?.key;
    const path = `${AR_MEDIA_PAGE.path}/${mediaId}${
      targetType ? `/${encodeURIComponent(targetType)}` : ""
    }`;
    if (editMediaStore.renderer.commands.canUndo) {
      notificationStore.open({
        title: localeStore.t('common.warnings["unsaved-changes-title"]'),
        message: localeStore.t('common.warnings["proceed-last-changes"]'),
        confirmText: localeStore.t("common.buttons.save"),
        cancelText: localeStore.t("common.buttons.cancel"),
        onOk: async () => {
          const mediaShortModel = editMediaStore.mediaShortModel;
          if (!mediaShortModel || !mediaShortModel?.file) return;

          const blob = await editMediaStore.getEditedImagePngBlob();
          if (!blob) return;
          const formData = new FormData();
          const fileName = mediaShortModel.file.filename.split(".").shift() + ".png";
          formData.append("files", blob, fileName);
          formData.append("type", MediaType.image);
          formData.append("metaData", JSON.stringify({ ...editMediaStore.metaData }));
          const result = await mediaStore.updateMedia(mediaId ? +mediaId : 0, formData);
          if (result.success) {
            navigate(path);
          }
        },
      });
      return;
    }
    navigate(path);
  };

  const handlePointerEnter = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!isProLocked) return;
    proFeatureTooltip(e.currentTarget, "model_image", { position: "left" });
  };

  const handlePointerLeave = () => {
    if (!isProLocked) tooltipStore.close();
  };

  const handleChooseTemplate = () => {
    if (isProLocked) return;
    const existingType = resolveExistingProductType();
    if (existingType) {
      openEditor(existingType.name || existingType.key);
      return;
    }
    setSelectedType(productTypesTemplates[0] || null);
    chooseTypeModal.setTruthy();
  };

  const handleConfirmProductType = () => {
    if (!selectedType) return;
    openEditor(selectedType.name);
    chooseTypeModal.setFalsy();
  };

  return (
    <Fragment>
      <GroupBlock className={styles.applyModelTemplateBlockGroup}>
        <div className={styles.titleRow}>
          <Typography size="small600" color="textSecondary">
            {localeStore.t('["edit-media"].inspector.background["model-template"].title')}
          </Typography>
          <SvgIcon
            className={styles.proBadge}
            icon={ProBadgeSVG}
            size={[28, 12]}
            disableAutoColor
          />
        </div>

        <Typography size="extraSmall" color="textTertiary">
          {localeStore.t('["edit-media"].inspector.background["model-template"].description')}
        </Typography>

        <Button
          className={styles.chooseButton}
          appearance="secondaryOutlined"
          onClick={handleChooseTemplate}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          disabled={isProLocked}
        >
          {localeStore.t('["edit-media"].inspector.background["model-template"].button')}
        </Button>
      </GroupBlock>

      <Modal
        isOpen={chooseTypeModal.value}
        setClose={chooseTypeModal.setFalsy}
        contentClassName={styles.chooseTypeModal}
        withCross
        name="choose-product-type-modal"
      >
        <Typography tag="h3" size="medium" color="textSecondary" className={styles.modalTitle}>
          {localeStore.t('["edit-media"].inspector.background["model-template"]["choose-title"]')}
        </Typography>

        <div className={styles.productTypeGrid}>
          {productTypesTemplates.map((category) => {
            const item = productTypesMap[category.name.toLowerCase()];

            if (!item) return;
            const isActive = selectedType?.name === category.name;
            return (
              <button
                key={item.type}
                className={clsx(styles.productTypeCard, { [styles.active]: isActive })}
                onClick={() => setSelectedType(category)}
              >
                <SvgIcon
                  icon={item.icon}
                  size={48}
                  className={clsx(styles.typeIcon, { [styles.active]: isActive })}
                />
                <Typography className={clsx(styles.typeText, { [styles.active]: isActive })}>
                  {localeStore.t(
                    `create["augmented-reality-item"]["product-type"].${
                      productDefaultTypesNames[item.label.toLowerCase()]
                    }` as LocaleCodeTypes,
                  )}
                </Typography>
              </button>
            );
          })}
        </div>

        <div className={styles.modalActions}>
          <Button
            className={styles.modalActionsButton}
            appearance="tertiaryOutlined"
            onClick={chooseTypeModal.setFalsy}
          >
            {localeStore.t("common.buttons.cancel")}
          </Button>
          <Button
            className={styles.modalActionsButton}
            onClick={handleConfirmProductType}
            disabled={!selectedType}
          >
            {localeStore.t("common.buttons.next")}
          </Button>
        </div>
      </Modal>
    </Fragment>
  );
});

export default ApplyModelTemplate;
